import { useMemo, useState } from "react";
import {
  useOpsEngagement,
  useProcessCasesByEngagement,
  useOperationalDocument,
  useEventLog,
  useCostLines,
  opsV2Request,
  type OpsV2Error,
} from "../../../api/opsV2";
import { hasPermissionInCurrentOrg } from "../../../state/orgContext";
import { AppShell } from "../../layouts/AppShell";
import { ForbiddenPanel } from "../../components/ops/ForbiddenPanel";
import { NotAccessiblePanel } from "../../components/ops/NotAccessiblePanel";
import { ArrowLeft, FolderOpen, FileText, History, DollarSign, RotateCcw } from "lucide-react";

const STATUS_LABELS: Record<string, string> = {
  pipeline: "Pipeline",
  accepted: "Accepté",
  active: "Actif",
  blocked: "Bloqué",
  completed: "Terminé",
  cancelled: "Annulé",
};

const CASE_STATUS_LABELS: Record<string, string> = {
  pending_decision: "En attente",
  waiting_start: "En attente démarrage",
  active: "Actif",
  blocked: "Bloqué",
  completed: "Terminé",
  cancelled: "Annulé",
  ignored: "Ignoré",
};

type EngagementDetailPageProps = {
  engagementId: string;
  userName?: string;
  onBackToList?: () => void;
  onOpenProcessCase?: (engagementId: string, processCaseId: string) => void;
};

export function EngagementDetailPage({
  engagementId,
  userName = "Utilisateur",
  onBackToList,
  onOpenProcessCase,
}: EngagementDetailPageProps) {
  const { data: engagement, loading: loadingEng, error: errorEng, refetch: refetchEng } = useOpsEngagement(engagementId);
  const { data: processCases, loading: loadingCases } = useProcessCasesByEngagement(engagementId);
  const canReadDoc = hasPermissionInCurrentOrg("finance.operational_document.read");
  const canReverseCost = hasPermissionInCurrentOrg("finance.cost.reverse");
  const { data: operationalDoc, loading: loadingDoc } = useOperationalDocument(canReadDoc ? engagementId : null);
  const { data: costLines, loading: loadingCostLines, refetch: refetchCostLines } = useCostLines(canReadDoc ? engagementId : null);
  const [reversingId, setReversingId] = useState<string | null>(null);
  const { data: eventLog, loading: loadingLog } = useEventLog({
    entityType: "ops_engagement",
    entityId: engagementId,
    limit: 20,
  });

  const apiError = errorEng as OpsV2Error | undefined;
  const is403 = apiError?.status === 403;
  const is404 = apiError?.status === 404;

  const modules = useMemo(
    () => [
      { label: "Tableau de bord", onClick: () => (window.location.hash = "#/") },
      { label: "Dossiers", onClick: onBackToList },
    ],
    [onBackToList]
  );

  if (is403) {
    return (
      <AppShell topbar={<div className="text-sm text-muted">Dossier</div>} userName={userName} modules={modules}>
        <ForbiddenPanel message={errorEng?.message} onBack={onBackToList} />
      </AppShell>
    );
  }

  if (is404 || (errorEng && !loadingEng)) {
    return (
      <AppShell topbar={<div className="text-sm text-muted">Dossier</div>} userName={userName} modules={modules}>
        <NotAccessiblePanel message={errorEng?.message} onBack={onBackToList} />
      </AppShell>
    );
  }

  if (loadingEng && !engagement) {
    return (
      <AppShell topbar={<div className="text-sm text-muted">Dossier</div>} userName={userName} modules={modules}>
        <div className="p-8 text-center text-sm text-muted-foreground">Chargement…</div>
      </AppShell>
    );
  }

  if (!engagement) return null;

  return (
    <AppShell
      topbar={<div className="text-sm text-muted">Dossier</div>}
      userName={userName}
      modules={modules}
    >
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          {onBackToList && (
            <button
              type="button"
              onClick={onBackToList}
              className="inline-flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-text hover:bg-surface-muted"
            >
              <ArrowLeft className="h-4 w-4" />
              Retour aux dossiers
            </button>
          )}
          <h1 className="text-section text-text">{engagement.title}</h1>
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium">
            {STATUS_LABELS[engagement.status] ?? engagement.status}
          </span>
        </div>

        <div className="rounded-lg border border-border bg-surface p-4">
          <h2 className="text-sm font-semibold text-text">Informations</h2>
          <dl className="mt-2 grid gap-2 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-muted-foreground">Type</dt>
              <dd className="font-medium text-text">{engagement.engagement_type}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Priorité</dt>
              <dd className="font-medium text-text">{engagement.priority ?? "—"}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Créé le</dt>
              <dd className="text-text">{engagement.created_at ? new Date(engagement.created_at).toLocaleString("fr-CA") : "—"}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Dernière MAJ</dt>
              <dd className="text-text">{engagement.updated_at ? new Date(engagement.updated_at).toLocaleString("fr-CA") : "—"}</dd>
            </div>
          </dl>
          {engagement.description && (
            <p className="mt-3 text-sm text-muted-foreground">{engagement.description}</p>
          )}
        </div>

        {/* Process cases */}
        <div className="rounded-lg border border-border bg-surface p-4">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-text">
            <FolderOpen className="h-4 w-4" />
            Dossiers de processus
          </h2>
          {loadingCases ? (
            <p className="mt-2 text-sm text-muted-foreground">Chargement…</p>
          ) : !processCases?.length ? (
            <p className="mt-2 text-sm text-muted-foreground">Aucun dossier de processus.</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {processCases.map((pc) => (
                <li
                  key={pc.id}
                  className="flex items-center justify-between rounded-md border border-border bg-surface-muted/30 p-3"
                >
                  <div>
                    <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium">
                      {CASE_STATUS_LABELS[pc.status] ?? pc.status}
                    </span>
                    <span className="ml-2 text-sm text-text">Version {pc.process_version_id?.slice(0, 8) ?? "—"}</span>
                  </div>
                  {onOpenProcessCase && (
                    <button
                      type="button"
                      onClick={() => onOpenProcessCase(engagementId, pc.id)}
                      className="text-sm font-medium text-accent hover:underline"
                    >
                      Ouvrir
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Operational document */}
        {canReadDoc && (
          <div className="rounded-lg border border-border bg-surface p-4">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-text">
              <FileText className="h-4 w-4" />
              Document opérationnel
            </h2>
            {loadingDoc ? (
              <p className="mt-2 text-sm text-muted-foreground">Chargement…</p>
            ) : operationalDoc ? (
              <dl className="mt-2 grid gap-2 text-sm sm:grid-cols-4">
                <div>
                  <dt className="text-muted-foreground">Revenu nominal</dt>
                  <dd className="font-medium text-text">
                    {operationalDoc.currency} {operationalDoc.revenue_nominal.toFixed(2)}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Coûts</dt>
                  <dd className="font-medium text-text">
                    {operationalDoc.currency} {operationalDoc.costs_total.toFixed(2)}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Marge</dt>
                  <dd className="font-medium text-text">
                    {operationalDoc.currency} {operationalDoc.margin.toFixed(2)}
                  </dd>
                </div>
              </dl>
            ) : (
              <p className="mt-2 text-sm text-muted-foreground">Non disponible.</p>
            )}
          </div>
        )}

        {/* Cost lines (Finance détaillé) */}
        {canReadDoc && (
          <div className="rounded-lg border border-border bg-surface p-4">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-text">
              <DollarSign className="h-4 w-4" />
              Lignes de coûts
            </h2>
            {loadingCostLines ? (
              <p className="mt-2 text-sm text-muted-foreground">Chargement…</p>
            ) : !costLines?.length ? (
              <p className="mt-2 text-sm text-muted-foreground">Aucune ligne de coût.</p>
            ) : (
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-muted">
                      <th className="py-2 pr-2">Type</th>
                      <th className="py-2 pr-2">Libellé</th>
                      <th className="py-2 pr-2 text-right">Quantité</th>
                      <th className="py-2 pr-2 text-right">Coût unit.</th>
                      <th className="py-2 pr-2 text-right">Total</th>
                      <th className="py-2 pr-2">Devise</th>
                      {canReverseCost && <th className="py-2 pr-2" />}
                    </tr>
                  </thead>
                  <tbody>
                    {costLines.map((cl) => (
                      <tr key={cl.id} className="border-b border-border/50">
                        <td className="py-2 pr-2">{cl.cost_type}</td>
                        <td className="py-2 pr-2">{cl.label}{cl.is_reversal ? " (contre-passation)" : ""}</td>
                        <td className="py-2 pr-2 text-right">{cl.quantity}</td>
                        <td className="py-2 pr-2 text-right">{cl.unit_cost.toFixed(2)}</td>
                        <td className="py-2 pr-2 text-right">{cl.total_amount.toFixed(2)}</td>
                        <td className="py-2 pr-2">{cl.currency}</td>
                        {canReverseCost && (
                          <td className="py-2 pr-2">
                            {!cl.is_reversal && (
                              <button
                                type="button"
                                disabled={reversingId === cl.id}
                                onClick={async () => {
                                  const reason = window.prompt("Raison (correction, warranty, adjustment, override) ?", "correction");
                                  if (reason == null) return;
                                  const justification = window.prompt("Justification (optionnel) ?");
                                  setReversingId(cl.id);
                                  try {
                                    await opsV2Request(`/cost-lines-v2/${cl.id}/reverse`, {
                                      method: "POST",
                                      body: { reason_code: reason || "correction", justification: justification || null },
                                    });
                                    refetchCostLines();
                                  } catch (e) {
                                    alert((e as Error).message);
                                  } finally {
                                    setReversingId(null);
                                  }
                                }}
                                className="inline-flex items-center gap-1 text-xs text-primary hover:underline disabled:opacity-50"
                              >
                                <RotateCcw className="h-3 w-3" />
                                Inverser
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Event log */}
        <div className="rounded-lg border border-border bg-surface p-4">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-text">
            <History className="h-4 w-4" />
            Journal
          </h2>
          {loadingLog ? (
            <p className="mt-2 text-sm text-muted-foreground">Chargement…</p>
          ) : !eventLog?.length ? (
            <p className="mt-2 text-sm text-muted-foreground">Aucun événement.</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {eventLog.map((ev) => (
                <li key={ev.id} className="flex flex-wrap items-baseline gap-2 text-sm">
                  <span className="text-muted-foreground">
                    {ev.created_at ? new Date(ev.created_at).toLocaleString("fr-CA") : ""}
                  </span>
                  <span className="rounded bg-muted px-1.5 py-0.5 text-xs">{ev.event_type}</span>
                  {ev.message && <span className="text-text">{ev.message}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </AppShell>
  );
}
